/* Copyright (c) 2020-present CloudIO Inc */

import { IoDatasource } from '@cloudio-saas/datasource-types';
import { useStateXAtomSetter, useStateXAtomValue } from '@cloudio-saas/statex';
import { Box, ListItem, ListItemText, Theme, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import VirtualList from 'components/DataGrid/grid/List';
import { useStoreRecordsOrder } from 'components/DataStoreReference/DataStoreReference';
import React, {
  CSSProperties,
  memo,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useAutoSizerContext } from 'hooks/useComponentSize';
import Store from 'store/Store';
import { useStoreCurrentRecordIdCallback } from 'store/useAppStore';
import { SimpleNoDataFound } from 'api/NoDataFound';
import {
  DSView,
  sidebarDataSourceFilterAtom,
  sidebarDataSourceFilterResultsSizeAtom,
} from '../../atoms';
import TimeAgo from 'react-timeago';
import DataSourceActions from './DataSourceActions';
import { IconPulse } from '../../../../utils/Pulse';
import { CellComponentProps } from '../../../../components/DataGrid/grid/types';
import { UserProfilePic } from 'components/UserPic/UserPic';
import Icon from 'icons/Icon';
import { TypographyEllipsisProps } from 'utils/commonProps';
import { isNotEmpty } from 'utils/isEmpty';

const useStyles = makeStyles((theme: Theme) => ({
  editIcon: {
    display: 'none',
  },
  listItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    '&:hover,&$active': {
      '& > $editIcon': {
        display: 'flex',
      },
    },
  },
  icon: {
    marginRight: 12,
    marginTop: 6,
    alignSelf: 'start',
    flexShrink: 0,
  },
  active: {
    backgroundColor: theme.palette.action.selected,
  },
  label: {
    flex: 1,
    userSelect: 'text',
  },
}));

function AppCell({
  value,
  style,
  setView,
  store,
  props,
  appUid,
  isViewOnlyUser = false,
}: {
  appUid: string;
  value: string;
  style?: CSSProperties;
  setView: (_view: DSView) => void;
  store: Store<IoDatasource>;
  props: React.HTMLAttributes<HTMLElement>;
  isViewOnlyUser: boolean;
}): React.ReactElement {
  const classes = useStyles();
  const [active, setActive] = useState(value === store.getCurrentRecordId());
  // use callback to avoid re-render every cell when the current record id changes
  useStoreCurrentRecordIdCallback(store, ({ value: newValue }) => {
    setActive(value === newValue);
  });
  const record = store.getRecord(value);

  return (
    <ListItem
      data-testid={record.datasourceUid}
      className={clsx(classes.listItem, active ? classes.active : null)}
      key={value}
      style={style}
      dense
    >
      <UserProfilePic
        userName={record.lastUpdatedBy}
        size={24}
        hideOnlineStatus={true}
      />
      <ListItemText
        primaryTypographyProps={TypographyEllipsisProps}
        secondaryTypographyProps={TypographyEllipsisProps}
        primary={
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography
              variant="body1"
              className={classes.label}
              noWrap
              data-nocapture
            >
              {record.datasourceUid}
              {record.lastUpdateDate ? (
                <Box
                  component="span"
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.8em',
                    ml: 1,
                    userSelect: 'none',
                  }}
                >
                  <TimeAgo date={record.lastUpdateDate} />
                </Box>
              ) : null}
            </Typography>
          </Box>
        }
        secondary={
          <>
            <Box
              sx={{
                display: 'flex',
                width: '100%',
                alignItems: 'center',
                gap: '4px',
                pt: '4px',
                userSelect: 'text',
              }}
            >
              {record.datasourceType === 'Table' ? (
                <Box
                  data-tip={`Table: ${record.typeValue}${
                    isNotEmpty(record.extTypeValue)
                      ? `\nPackage: ${record.extTypeValue}`
                      : ''
                  } ${
                    isNotEmpty(record.objectConnectionCode)
                      ? `\nObject Connection: ${record.objectConnectionCode}`
                      : ''
                  } `}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    userSelect: 'text',
                  }}
                >
                  <Icon iconName={'table'} height={12} width={12} />
                  {record.typeValue}
                  {record.objectConnectionCode ? (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        userSelect: 'text',
                      }}
                    >
                      <Icon
                        iconName={'plug-circle-check'}
                        height={12}
                        width={12}
                      />
                      {record.objectConnectionCode}
                    </Box>
                  ) : null}
                  {isNotEmpty(record.extTypeValue) ? (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        userSelect: 'text',
                      }}
                    >
                      <Icon
                        iconName={'cube'}
                        iconType={'duotone'}
                        height={12}
                        width={12}
                      />
                      {record.extTypeValue}
                    </Box>
                  ) : null}
                </Box>
              ) : (
                <Box
                  data-tip={`External Datasource: ${record.typeValue}${
                    isNotEmpty(record.extTypeValue)
                      ? `\nExternal Object: ${record.extTypeValue}`
                      : ''
                  } `}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    userSelect: 'text',
                  }}
                >
                  <Icon
                    iconName={'database'}
                    iconType={'duotone'}
                    height={12}
                    width={12}
                  />
                  {record.typeValue}
                  {isNotEmpty(record.extTypeValue) ? (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        userSelect: 'text',
                      }}
                    >
                      <Icon
                        iconName={'circle-info'}
                        iconType={'duotone'}
                        height={12}
                        width={12}
                      />
                      {record.extTypeValue}
                    </Box>
                  ) : null}
                </Box>
              )}
            </Box>
          </>
        }
      />
      {record.hasQueryScript === 'Y' ? (
        <Box sx={{ position: 'relative' }}>
          <Typography
            data-tip="Has Query Script"
            sx={{ marginRight: 1, color: 'text.secondary' }}
            variant="caption"
          >
            Q
          </Typography>
          <IconPulse
            pulseKey="ds_q"
            pulseMessage="Q indicates that this datasource has either a pre or post query script defined."
          />
        </Box>
      ) : null}
      {record.hasBeforeScript === 'Y' ? (
        <Box sx={{ position: 'relative' }}>
          <Typography
            data-tip="Has Before Script"
            sx={{ marginRight: 1, color: 'text.secondary' }}
            variant="caption"
          >
            B
          </Typography>
          <IconPulse
            pulseKey="ds_b"
            pulseMessage="B indicates that this datasource has either a before insert, update or delete script defined."
          />
        </Box>
      ) : null}
      {record.hasAfterScript === 'Y' ? (
        <Typography
          data-tip="Has After Script"
          sx={{ marginRight: 1, color: 'text.secondary' }}
          variant="caption"
        >
          A
        </Typography>
      ) : null}
      <DataSourceActions
        key={value}
        className={classes.editIcon}
        setView={setView}
        store={store}
        value={value}
        appUid={appUid}
        isViewOnlyUser={isViewOnlyUser}
      />
    </ListItem>
  );
}

const MemoAppCell = memo(AppCell);

const DataSourceList = ({
  setView,
  store,
  appUid,
  isViewOnlyUser = false,
}: {
  setView: (_view: DSView) => void;
  store: Store<IoDatasource>;
  appUid: string;
  isViewOnlyUser: boolean;
}) => {
  store.readData(); // suspend until data is loaded
  let recordsOrder = useStoreRecordsOrder(store) as string[];
  let filter = useStateXAtomValue(sidebarDataSourceFilterAtom);
  const setSize = useStateXAtomSetter(sidebarDataSourceFilterResultsSizeAtom);

  if (filter) {
    if (filter.startsWith('ds:')) {
      filter = filter.substring(3);
      recordsOrder = recordsOrder.filter((id) => {
        const r = store.getRecord(id);
        return r.datasourceUid === filter;
      });
    } else {
      const lcfilter = filter.toLowerCase();
      recordsOrder = recordsOrder.filter((id) => {
        const r = store.getRecord(id);
        return (
          r.datasourceUid?.toLowerCase().includes(lcfilter) ||
          r.typeValue?.toLowerCase().includes(lcfilter) ||
          r.extTypeValue?.toLowerCase().includes(lcfilter)
        );
      });
    }
  }
  const size = recordsOrder.length;
  useEffect(() => {
    setSize(size);
  }, [setSize, size]);

  // useCallback to avoid re-render the grid
  const CellComponent = useCallback(
    ({ rowIndex, style, ...others }: CellComponentProps) => {
      return (
        <MemoAppCell
          appUid={appUid}
          style={style}
          value={recordsOrder[rowIndex]}
          setView={setView}
          store={store}
          props={others}
          isViewOnlyUser={isViewOnlyUser}
        />
      );
    },
    [appUid, isViewOnlyUser, recordsOrder, setView, store],
  );
  const { width, height } = useAutoSizerContext();

  if (recordsOrder.length === 0) {
    return (
      <SimpleNoDataFound
        msg={
          filter
            ? `No data source found matching "${filter}"!`
            : 'No data sources defined yet!'
        }
      />
    );
  }

  return (
    <VirtualList
      CellComponent={CellComponent}
      rowHeight={54}
      height={height}
      totalRows={recordsOrder.length}
      width={width}
      custom
    />
  );
};

export default memo(DataSourceList);
